import type { ClusterDetail, KeywordFeatures, KeywordRow } from '@radar/contracts';
import { z } from 'zod';
import type { KeywordRow as KeywordTableRow } from '@/components/domain/KeywordTable';
import type { SerpItem, SerpSnapshotSummary } from '@/components/domain/SerpResultList';
import { trendSlope } from '@/lib/trend';

// Parties de `GET /clusters/:id` que `@radar/contracts` ne type pas encore : `snapshots`,
// `analyses`, `mergeSuggestions` sont `unknown[]` (T18, T17/T20, T19 les remplissent), et
// `triage`, `supply`, `softFlags`, `mergedIntoClusterId`, `opportunity` n'y figurent pas (T17,
// T19, T22). Ces schémas lisent ces champs à la frontière, de façon tolérante (`unknown` → zod,
// `conventions.md` § zod) ; ils migrent dans `packages/contracts` quand ces tâches livrent la
// forme définitive. Un champ absent ou invalide vaut `undefined` / `[]` : la fiche masque la section.

export const PRODUCT_TYPES = ['web_app', 'browser_extension', 'api', 'mobile_app', 'none'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const INTENTS = [
  'task',
  'tool',
  'comparison',
  'purchase',
  'informational',
  'navigational',
  'mixed',
] as const;

export const EXCLUSION_CATEGORIES = [
  'none',
  'adult',
  'gambling',
  'health',
  'finance',
  'legal',
  'other_risky',
] as const;

/** Copie du dernier item de triage (`llm.md` § Triage, `clusters.triage`). */
export const ClusterTriage = z.object({
  is_job: z.boolean(),
  job_statement: z.string().nullable(),
  intent: z.enum(INTENTS).catch('mixed'),
  product_type_fit: z.enum(PRODUCT_TYPES).catch('none'),
  digital_product_possible: z.boolean(),
  is_brand_dominated: z.boolean(),
  exclusion_category: z.enum(EXCLUSION_CATEGORIES).catch('none'),
  triage_score: z.number(),
  confidence: z.number(),
  reason: z.string(),
});
export type ClusterTriage = z.infer<typeof ClusterTriage>;

/** `clusters.supply` (`scoring.md` § 1, T19). */
export const ClusterSupply = z.object({
  serp_count: z.number(),
  tool_ratio: z.number(),
  content_ratio: z.number(),
  ugc_ratio: z.number(),
  marketplace_ratio: z.number(),
  big_brand_ratio: z.number(),
  domain_concentration: z.number(),
  ads_top_avg: z.number(),
  ads_any_ratio: z.number(),
  paa_count: z.number(),
  overlap_within: z.number().nullable(),
});
export type ClusterSupply = z.infer<typeof ClusterSupply>;

const SnapshotFeatures = z.object({
  paa_questions: z.array(z.string()).optional(),
  paaQuestions: z.array(z.string()).optional(),
  has_featured_snippet: z.boolean().optional(),
  hasFeaturedSnippet: z.boolean().optional(),
});

const SnapshotItem = z.object({
  id: z.number().optional(),
  type: z.string(),
  rankAbsolute: z.number(),
  rankGroup: z.number().optional(),
  domain: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  classification: z.string().nullable().optional(),
});

/** Snapshot SERP de `GET /clusters/:id` (forme annoncée par la fiche T19). */
export const ClusterSnapshot = z.object({
  id: z.string(),
  keywordText: z.string().optional(),
  keyword: z.string().optional(),
  fetchedAt: z.string().nullable().optional(),
  mode: z.enum(['standard', 'live']).catch('standard'),
  status: z.string().optional(),
  seResultsCount: z.number().nullable().optional(),
  itemTypes: z.array(z.string()).optional(),
  paidTopCount: z.number().optional(),
  organicCount: z.number().optional(),
  features: SnapshotFeatures.optional(),
  items: z.array(SnapshotItem).catch([]),
});
export type ClusterSnapshot = z.infer<typeof ClusterSnapshot>;

/** Analyse LLM (`llm_analyses`, forme camelCase de la fiche T20). */
export const ClusterAnalysis = z.object({
  id: z.string(),
  tier: z.enum(['triage', 'deep']).catch('deep'),
  model: z.string(),
  promptVersion: z.string(),
  status: z.enum(['ok', 'invalid', 'failed']).catch('ok'),
  costUsd: z.number().nullable().optional(),
  createdAt: z.string(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  error: z.string().nullable().optional(),
});
export type ClusterAnalysis = z.infer<typeof ClusterAnalysis>;

/** Suggestion de fusion (`cluster_merge_suggestions`, forme de la fiche T19). */
export const ClusterMergeSuggestion = z.object({
  id: z.string(),
  otherClusterId: z.string(),
  otherLabel: z.string(),
  overlap: z.number(),
  status: z.enum(['open', 'accepted', 'dismissed']).catch('open'),
});
export type ClusterMergeSuggestion = z.infer<typeof ClusterMergeSuggestion>;

export const ClusterOpportunityLink = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
});
export type ClusterOpportunityLink = z.infer<typeof ClusterOpportunityLink>;

const ClusterExtrasFields = z.object({
  triage: ClusterTriage.nullable().optional(),
  supply: ClusterSupply.nullable().optional(),
  softFlags: z.array(z.string()).optional(),
  mergedIntoClusterId: z.string().nullable().optional(),
  mergedInto: z.object({ id: z.string(), label: z.string() }).nullable().optional(),
  opportunity: ClusterOpportunityLink.nullable().optional(),
});

export type ClusterExtras = {
  triage: ClusterTriage | null;
  supply: ClusterSupply | null;
  softFlags: string[];
  mergedInto: { id: string; label: string | null } | null;
  opportunity: ClusterOpportunityLink | null;
  snapshots: ClusterSnapshot[];
  analyses: ClusterAnalysis[];
  mergeSuggestions: ClusterMergeSuggestion[];
};

function readField<T>(schema: z.ZodType<T>, value: unknown): T | undefined {
  const result = schema.safeParse(value);
  return result.success ? result.data : undefined;
}

function parseList<T>(schema: z.ZodType<T>, value: unknown): T[] {
  if (!Array.isArray(value)) return [];
  const items: T[] = [];
  for (const item of value) {
    const parsed = schema.safeParse(item);
    if (parsed.success) items.push(parsed.data);
  }
  return items;
}

/** Lit les champs hors contrat d'une fiche cluster ; tolérant champ par champ. */
export function parseClusterExtras(raw: unknown): ClusterExtras {
  const record = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const shape = ClusterExtrasFields.shape;
  const triage = readField(shape.triage, record.triage) ?? null;
  const supply = readField(shape.supply, record.supply) ?? null;
  const softFlags = readField(shape.softFlags, record.softFlags) ?? [];
  const mergedIntoId = readField(shape.mergedIntoClusterId, record.mergedIntoClusterId) ?? null;
  const mergedInto = readField(shape.mergedInto, record.mergedInto) ?? null;
  const opportunity = readField(shape.opportunity, record.opportunity) ?? null;
  return {
    triage,
    supply,
    softFlags,
    mergedInto: mergedInto ?? (mergedIntoId ? { id: mergedIntoId, label: null } : null),
    opportunity,
    snapshots: parseList(ClusterSnapshot, record.snapshots),
    analyses: parseList(ClusterAnalysis, record.analyses),
    mergeSuggestions: parseList(ClusterMergeSuggestion, record.mergeSuggestions),
  };
}

/** Variantes détaillées d'un primaire si l'API les fournit (le contrat T14 ne porte que `variantCount`). */
const KeywordVariants = z.object({
  variants: z.array(
    z.object({ id: z.number(), text: z.string(), avgMonthlySearches: z.number().nullable() }),
  ),
});

export function parseKeywordVariants(raw: unknown) {
  return readField(KeywordVariants, raw)?.variants;
}

/** Chips de features (`components.md` § KeywordTable) dans l'ordre d'affichage. */
export function featureChips(features: KeywordFeatures): string[] {
  const chips: string[] = [];
  if (features.has_task_verb) chips.push('task_verb');
  if (features.modifiers.length > 0) chips.push('modifier');
  if (features.is_question) chips.push('question');
  if (features.has_file_format) chips.push('file_format');
  if (features.has_price_marker) chips.push('price');
  return chips;
}

/** Série mensuelle ordonnée (année, mois) → volumes. */
export function monthlyPoints(
  series: readonly { year: number; month: number; searches: number }[],
) {
  return [...series]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((point) => point.searches);
}

export function toKeywordTableRow(
  row: KeywordRow,
  representatives: ReadonlySet<number>,
): KeywordTableRow {
  const monthly = monthlyPoints(row.monthlySearches);
  const variants = parseKeywordVariants(row);
  return {
    id: row.id,
    text: row.text,
    avgMonthlySearches: row.avgMonthlySearches,
    highTopOfPageBidUsd: row.highTopOfPageBidUsd,
    competitionIndex: row.competitionIndex,
    monthly,
    features: featureChips(row.features),
    isRepresentative: representatives.has(row.id),
    variantCount: row.variantCount,
    ...(variants ? { variants } : {}),
    trendSlope: trendSlope(monthly),
  };
}

/** Somme des variantes repliées des lignes chargées. */
export function variantTotal(rows: readonly KeywordRow[]): number {
  return rows.reduce((sum, row) => sum + row.variantCount, 0);
}

export function toKeywordTableRows(
  rows: readonly KeywordRow[],
  representatives: readonly number[],
): KeywordTableRow[] {
  const set = new Set(representatives);
  return rows.map((row) => toKeywordTableRow(row, set));
}

/** Props de `SerpResultList` depuis un snapshot de la fiche. */
export function toSerpResultListProps(snapshot: ClusterSnapshot): {
  snapshot: SerpSnapshotSummary;
  items: SerpItem[];
} {
  const features = snapshot.features ?? {};
  return {
    snapshot: {
      id: snapshot.id,
      keyword: snapshot.keywordText ?? snapshot.keyword ?? snapshot.id,
      fetchedAt: snapshot.fetchedAt ?? new Date(0).toISOString(),
      mode: snapshot.mode,
      seResultsCount: snapshot.seResultsCount ?? null,
      itemTypes: snapshot.itemTypes ?? [],
      paidTopCount: snapshot.paidTopCount ?? 0,
      features: {
        paaQuestions: features.paaQuestions ?? features.paa_questions,
        hasFeaturedSnippet: features.hasFeaturedSnippet ?? features.has_featured_snippet,
      },
    },
    items: snapshot.items.map((item, index) => ({
      id: item.id ?? index,
      type: item.type,
      rankAbsolute: item.rankAbsolute,
      rankGroup: item.rankGroup ?? item.rankAbsolute,
      domain: item.domain ?? '',
      url: item.url ?? '',
      title: item.title ?? item.domain ?? '',
      description: item.description ?? null,
      classification: item.classification ?? null,
    })),
  };
}

/** Corps de `POST /clusters/:id/promote` (`api.md`, fiche T22) ; messages = clés i18n. */
export const PromoteInput = z.object({
  title: z.string().trim().min(3, 'validation.minLength').max(200, 'validation.maxLength'),
  canonicalJob: z.string().trim().min(3, 'validation.minLength').max(500, 'validation.maxLength'),
  targetSegment: z.string().trim().min(2, 'validation.minLength').max(200, 'validation.maxLength'),
  productType: z.enum(PRODUCT_TYPES),
  rationale: z.string().trim().min(20, 'validation.minLength').max(2000, 'validation.maxLength'),
});
export type PromoteInput = z.infer<typeof PromoteInput>;

export const PromoteResponse = z.object({ id: z.string() });
export type PromoteResponse = z.infer<typeof PromoteResponse>;

export const MergeInput = z.object({
  intoClusterId: z.string().min(1),
  rationale: z.string().trim().min(20, 'validation.minLength'),
});
export type MergeInput = z.infer<typeof MergeInput>;

export const JobResponse = z.object({ jobId: z.string(), existing: z.boolean().optional() });
export type JobResponse = z.infer<typeof JobResponse>;

export type ClusterDetailData = ClusterDetail;
